/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-12 13:44:49
 *
 *******************************************************/
// @flow
import _ from 'lodash';

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';
import { getAppAdminFeatures } from 'src/helpers/features-helper';

import apiService from 'src/services/api-service';
import {
  REVERT_THEME_REQUEST,
  REVERT_THEME_SUCCESS,
  REVERT_THEME_FAILURE,
  REVERT_THEME_FEEDBACK_REQUEST,
  REVERT_THEME_FEEDBACK_SUCCESS,
  REVERT_THEME_FEEDBACK_FAILURE,
  REVERT_THEME_MANUAL_CONTACT_REQUEST,
  REVERT_THEME_MANUAL_CONTACT_SUCCESS,
  REVERT_THEME_MANUAL_CONTACT_FAILURE,
  RESET_THEME_REVERT
} from 'stores/flow-type/theme-revert.type';
import { trackFSEvent } from 'src/integrations/fullStory';

/**
 * call to revert theme api
 * @return {object} The RSAA object for API middleware
 */
export const revertTheme = (body: Object, i18n: I18n) => {
  const themeSetupV2 = getAppAdminFeatures('themeSetupV2');

  const { themeId, removeSearchOnly = false, themeName = '' } = body;

  const route = themeSetupV2
    ? 'admin-v3.theme-setup.revert-theme'
    : 'admin-v2.setup-requests.revert-theme';

  return apiService.sendRequest(route, { body }, [
    REVERT_THEME_REQUEST,
    {
      type: REVERT_THEME_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom',
          i18n.translate('AdminHTML.ThemeSetup.Actions.revertTheme_Success'),
          i18n
        );

        // Track Full Story event
        if (removeSearchOnly) {
          trackFSEvent('Remove Search on Theme', {
            feature: 'Theme Setup',
            selectedTheme: themeName
          });
        } else {
          trackFSEvent('Remove All Features', {
            feature: 'Theme Setup',
            selectedTheme: themeName
          });
        }

        return { data, themeId, removeSearchOnly };
      }
    },
    {
      type: REVERT_THEME_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.revertTheme_Failure'),
          i18n
        );

        return _.assign(errorResult, { themeId, removeSearchOnly });
      }
    }
  ]);
};

/**
 * Submit feedback after theme revert
 * @return {object} The RSAA object for API middleware
 */
export const sendThemeRevertFeedback = (body: Object, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.theme-setup.revert-theme-feedback', { body }, [
    REVERT_THEME_FEEDBACK_REQUEST,
    {
      type: REVERT_THEME_FEEDBACK_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: REVERT_THEME_FEEDBACK_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeRevert.Actions.sendThemeRevertFeedback_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * Submit feedback while theme revert and choose contact our team
 * @return {object} The RSAA object for API middleware
 */
export const sendThemeRevertContactOurTeamManual = (body: Object, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.theme-setup.revert-theme-contact-our-team-manual',
    { body },
    [
      REVERT_THEME_MANUAL_CONTACT_REQUEST,
      {
        type: REVERT_THEME_MANUAL_CONTACT_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'custom',
            i18n.translate(
              'AdminHTML.ThemeRevert.Actions.sendThemeRevertContactOurTeamManual_Success'
            ),
            i18n
          );
          return { data };
        }
      },
      {
        type: REVERT_THEME_MANUAL_CONTACT_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate(
              'AdminHTML.ThemeRevert.Actions.sendThemeRevertContactOurTeamManual_Failure'
            ),
            i18n
          )
      }
    ]
  );
};

/**
 * call to reset state
 * @return {object} The RSAA object for API middleware
 */
export const unmountComponent = () => {
  return { type: RESET_THEME_REVERT };
};
